import {
  DayOfWeek,
  EmailScheduleFrequency,
  EmailScheduleModel,
  EmailScheduleSource,
  UpsertEmailScheduleRequest,
} from '../../../Generated/Mark-It-View-Api';
import { EmailScheduleDraft, EmailScheduleDraftErrors } from '../../../Types/EmailScheduleDraft';

export type { EmailScheduleDraft, EmailScheduleDraftErrors };

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

export function browserTimeZoneId(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function createEmptyEmailScheduleDraft(): EmailScheduleDraft {
  return {
    emailScheduleId: 0,
    enabled: false,
    recipients: [],
    subject: '',
    frequency: EmailScheduleFrequency.Weekdays,
    dayOfWeek: DayOfWeek.Monday,
    dayOfMonth: 1,
    timeOfDay: '07:00',
    timeZoneId: browserTimeZoneId(),
  };
}

export function emailScheduleDraftFromModel(model: EmailScheduleModel | null | undefined): EmailScheduleDraft {
  const empty = createEmptyEmailScheduleDraft();

  if (!model) {
    return empty;
  }

  return {
    emailScheduleId: model.emailScheduleId,
    enabled: model.isActive,
    recipients: [...model.recipients],
    subject: model.subject ?? '',
    frequency: model.frequency,
    dayOfWeek: model.dayOfWeek ?? empty.dayOfWeek,
    dayOfMonth: model.dayOfMonth ?? empty.dayOfMonth,
    timeOfDay: model.timeOfDay,
    timeZoneId: model.timeZoneId,
  };
}

export function parseRecipients(text: string): string[] {
  const seen = new Set<string>();
  const recipients: string[] = [];

  for (const part of text.split(/[,;\s]+/)) {
    const trimmed = part.trim();
    if (!trimmed) {
      continue;
    }

    const key = trimmed.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      recipients.push(trimmed);
    }
  }

  return recipients;
}

export function isValidEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value);
}

export function validateEmailScheduleDraft(draft: EmailScheduleDraft): EmailScheduleDraftErrors {
  const errors: EmailScheduleDraftErrors = {};

  if (!draft.enabled) {
    return errors;
  }

  if (draft.recipients.length === 0) {
    errors.recipients = 'Add at least one recipient';
  } else {
    const invalid = draft.recipients.filter((recipient) => !isValidEmail(recipient));
    if (invalid.length > 0) {
      errors.recipients = `Not a valid email address: ${invalid.join(', ')}`;
    }
  }

  if (draft.frequency === EmailScheduleFrequency.Monthly && (draft.dayOfMonth < 1 || draft.dayOfMonth > 31)) {
    errors.dayOfMonth = 'Day of month must be between 1 and 31';
  }

  if (!TIME_PATTERN.test(draft.timeOfDay)) {
    errors.timeOfDay = 'Enter a time as HH:mm';
  }

  return errors;
}

export function hasEmailScheduleErrors(errors: EmailScheduleDraftErrors): boolean {
  return Object.values(errors).some((error) => !!error);
}

export function toUpsertEmailScheduleRequest(
  draft: EmailScheduleDraft,
  sourceId: number,
  source: EmailScheduleSource = EmailScheduleSource.MarketViewSavedQuery,
  siteGuid: string | null = null,
): UpsertEmailScheduleRequest {
  return {
    emailScheduleId: draft.emailScheduleId,
    source,
    sourceId,
    siteGuid,
    subject: draft.subject.trim() === '' ? null : draft.subject.trim(),
    recipients: draft.recipients,
    frequency: draft.frequency,
    dayOfWeek: draft.frequency === EmailScheduleFrequency.Weekly ? draft.dayOfWeek : undefined,
    dayOfMonth: draft.frequency === EmailScheduleFrequency.Monthly ? draft.dayOfMonth : null,
    timeOfDay: draft.timeOfDay,
    timeZoneId: draft.timeZoneId,
    isActive: true,
  };
}

function ordinal(day: number): string {
  const remainderHundred = day % 100;
  if (remainderHundred >= 11 && remainderHundred <= 13) {
    return `${day}th`;
  }

  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
}

export function describeEmailSchedule(schedule: Pick<EmailScheduleModel, 'frequency' | 'dayOfWeek' | 'dayOfMonth' | 'timeOfDay' | 'timeZoneId' | 'recipients' | 'isActive'>): string {
  let when: string;

  switch (schedule.frequency) {
    case EmailScheduleFrequency.Daily:
      when = 'Every day';
      break;
    case EmailScheduleFrequency.Weekdays:
      when = 'Weekdays';
      break;
    case EmailScheduleFrequency.Weekly:
      when = `Every ${schedule.dayOfWeek ?? 'week'}`;
      break;
    case EmailScheduleFrequency.Monthly:
      when = `Monthly on the ${ordinal(schedule.dayOfMonth ?? 1)}`;
      break;
    default:
      when = schedule.frequency;
  }

  const recipientCount = schedule.recipients.length;
  const recipients = recipientCount === 1 ? '1 recipient' : `${recipientCount} recipients`;
  const status = schedule.isActive ? '' : ' (paused)';

  return `${when} at ${schedule.timeOfDay} ${schedule.timeZoneId} to ${recipients}${status}`;
}
